/**
 * 家长端首页：孩子卡片列表。
 * 区分"我创建的"与"共享给我的"（见方案 1.2 ⑧：owner 与 editor/viewer 是两回事），
 * 并显示待处理共享邀请的数量角标。
 */
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AppShell } from '../../components/layout/AppShell';
import { Badge, Card, CardHeader, EmptyState, Skeleton } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { StudentCard } from '../../components/parent/StudentCard';
import { CreateStudentModal } from '../../components/parent/CreateStudentModal';
import { useShares, useSharedStudents, useStudents } from '../../lib/queries';
import { useAuth } from '../../lib/auth';

export function ParentHomePage() {
  const { user } = useAuth();
  const [createOpen, setCreateOpen] = useState(false);

  const studentsQuery = useStudents();
  const sharedStudentsQuery = useSharedStudents();
  const sharesQuery = useShares();

  const pendingCount = sharesQuery.data?.incoming.length ?? 0;

  const { owned, shared } = useMemo(() => {
    const mine = (studentsQuery.data ?? []).filter((s) => s.permission === 'owner');
    // "共享给我的"直接用 /students 里 permission 非 owner 的项
    // （后端没有 /shares/students 聚合端点，见 lib/api/shares.ts 的说明）
    const sharedList =
      sharedStudentsQuery.data && sharedStudentsQuery.data.length > 0
        ? sharedStudentsQuery.data
        : (studentsQuery.data ?? []).filter((s) => s.permission !== 'owner');
    return { owned: mine, shared: sharedList };
  }, [studentsQuery.data, sharedStudentsQuery.data]);

  const loading = studentsQuery.isLoading;
  /**
   * 角标挂在**「工具」**上（原来是挂在 `/shares` 上）：
   * 共享收件箱（别人把孩子共享给我、待我同意）现在住在 `/tools` 里
   * （见 `ToolsPage`），那个数字就该指着那一格。
   */
  const badges = pendingCount > 0 ? { '/tools': pendingCount } : undefined;

  return (
    <AppShell
      kind="parent"
      title="我的孩子"
      {...(badges ? { badges } : {})}
      headerAction={
        <Button size="sm" onClick={() => setCreateOpen(true)}>
          添加孩子
        </Button>
      }
    >
      <div className="space-y-6">
        {user?.mustChangePassword && (
          <div className="rounded-2xl bg-amber-50 px-4 py-3 text-sm text-amber-800">
            你的密码是系统初始密码，为了安全请尽快
            <Link to="/settings" className="mx-1 font-semibold underline">
              修改密码
            </Link>
            。
          </div>
        )}

        {pendingCount > 0 && (
          <Link
            to="/tools"
            className="flex items-center justify-between rounded-2xl border border-brand-200 bg-brand-50 px-4 py-3"
          >
            <span className="text-sm font-medium text-brand-800">
              有 {pendingCount} 个共享邀请等待你处理
            </span>
            <span aria-hidden="true" className="text-brand-600">
              ›
            </span>
          </Link>
        )}

        {/* ---------- 我创建的 ---------- */}
        <section aria-labelledby="owned-heading" className="space-y-3">
          <div className="flex items-center justify-between">
            <h2 id="owned-heading" className="text-sm font-semibold uppercase tracking-wide text-slate-500">
              我创建的
            </h2>
            <Badge>{owned.length}</Badge>
          </div>

          {loading ? (
            <div className="space-y-3">
              <Skeleton className="h-20 w-full" />
              <Skeleton className="h-20 w-full" />
            </div>
          ) : owned.length === 0 ? (
            <EmptyState
              icon="🧒"
              title="还没有孩子账号"
              description="创建孩子账号后，他会用自己的用户名登录学生端打卡，你可以在手机上查看进度。"
              action={
                <Button className="mt-2" onClick={() => setCreateOpen(true)}>
                  添加第一个孩子
                </Button>
              }
            />
          ) : (
            <ul className="space-y-3">
              {owned.map((student) => (
                <li key={student.id}>
                  <StudentCard student={student} />
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* ---------- 共享给我的 ---------- */}
        <section aria-labelledby="shared-heading" className="space-y-3">
          <div className="flex items-center justify-between">
            <h2
              id="shared-heading"
              className="text-sm font-semibold uppercase tracking-wide text-slate-500"
            >
              共享给我的
            </h2>
            <Badge>{shared.length}</Badge>
          </div>

          {shared.length === 0 ? (
            <Card className="text-sm text-slate-500">
              其他家长把孩子共享给你后，会出现在这里。邀请与授权在
              <Link to="/tools" className="mx-1 font-medium text-brand-600 hover:underline">
                工具
              </Link>
              页，或那个孩子的详情页里。
            </Card>
          ) : (
            <ul className="space-y-3">
              {shared.map((student) => (
                <li key={student.id}>
                  <StudentCard student={student} />
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* ---------- 快捷入口 ---------- */}
        <section aria-labelledby="quick-heading" className="space-y-3">
          <h2
            id="quick-heading"
            className="text-sm font-semibold uppercase tracking-wide text-slate-500"
          >
            快捷入口
          </h2>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            <Link to="/tools" className="card flex flex-col gap-1 hover:shadow-md">
              <span aria-hidden="true" className="text-2xl">
                🤝
              </span>
              <span className="font-medium text-slate-800">工具与共享</span>
              <span className="text-xs text-slate-500">
                {pendingCount > 0 ? `${pendingCount} 个共享邀请待处理` : '听写等全家共用的小工具'}
              </span>
            </Link>
            <Link to="/rewards" className="card flex flex-col gap-1 hover:shadow-md">
              <span aria-hidden="true" className="text-2xl">
                🎁
              </span>
              <span className="font-medium text-slate-800">奖品目录</span>
              <span className="text-xs text-slate-500">用积分兑换的奖励</span>
            </Link>
            <Link to="/settings" className="card flex flex-col gap-1 hover:shadow-md">
              <span aria-hidden="true" className="text-2xl">
                ⚙️
              </span>
              <span className="font-medium text-slate-800">账号设置</span>
              <span className="text-xs text-slate-500">密码与登录设备</span>
            </Link>
          </div>

          {/* 孩子专属的入口：目标 / 打卡任务 / 统计 / 兑换审批都要先定位到孩子 */}
          <div className="rounded-2xl border border-slate-200 bg-white p-4">
            <p className="text-sm font-semibold text-slate-800">按孩子进入</p>
            <p className="mt-0.5 text-xs text-slate-500">
              目标、打卡任务配置、统计、兑换审批都在孩子的详情页里。
            </p>
            {owned.length + shared.length === 0 ? (
              <p className="mt-2 text-sm text-slate-400">先添加一个孩子账号，这里会出现快捷入口。</p>
            ) : (
              <ul className="mt-3 space-y-3">
                {[...owned, ...shared].map((student) => (
                  <li key={student.id} className="rounded-xl bg-slate-50 p-3">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <span className="font-medium text-slate-800">{student.nickname}</span>
                      <Link
                        to={`/students/${student.id}`}
                        className="text-xs font-medium text-brand-600 hover:underline"
                      >
                        全部管理 →
                      </Link>
                    </div>
                    <div className="mt-2 flex flex-wrap gap-2 text-xs">
                      <Link
                        to={`/students/${student.id}/nodes`}
                        className="rounded-lg bg-white px-2.5 py-1.5 font-medium text-slate-700 ring-1 ring-slate-200 hover:bg-slate-100"
                      >
                        🎯 目标
                      </Link>
                      {/*
                        「打卡任务」入口 2026-10-31 去掉：那一页（打卡任务总览）已删除，
                        它的两块内容（调整顺序 / 看孩子看到的清单）都在「目标」页底部。
                      */}
                      <Link
                        to={`/students/${student.id}/stats`}
                        className="rounded-lg bg-white px-2.5 py-1.5 font-medium text-slate-700 ring-1 ring-slate-200 hover:bg-slate-100"
                      >
                        📊 统计
                      </Link>
                      <Link
                        to={`/students/${student.id}/redemptions`}
                        className="rounded-lg bg-white px-2.5 py-1.5 font-medium text-slate-700 ring-1 ring-slate-200 hover:bg-slate-100"
                      >
                        🎁 兑换审批
                      </Link>
                      <Link
                        to={`/students/${student.id}/points`}
                        className="rounded-lg bg-white px-2.5 py-1.5 font-medium text-slate-700 ring-1 ring-slate-200 hover:bg-slate-100"
                      >
                        ⭐ 积分流水
                      </Link>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>

        <Card>
          <CardHeader
            title="关于积分"
            description="完成打卡任务获得积分，积分可以兑换奖品；家长也可以手动奖励或扣分。"
          />
          <p className="text-sm text-slate-500">
            积分规则在孩子详情页与奖品页可见，所有变动都会记录在积分流水里，孩子可以完全查看。
          </p>
        </Card>
      </div>

      <CreateStudentModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={() => void studentsQuery.refetch()}
      />
    </AppShell>
  );
}
